import { Select, Option } from '@material-tailwind/react';
import { createValidityState } from 'hooks/forms';
import {
  Children,
  ComponentProps,
  JSX,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

type SelectedElement = ReactElement<{
  className?: string;
  children?: ReactNode;
}>;

type Props = Omit<
  ComponentProps<typeof Select>,
  'error' | 'children' | 'selected'
> & {
  onValidate?: (validity: { valid: boolean }) => void | string;
  required?: boolean;
  placeholder?: ReactNode;
  children: Array<JSX.Element>;
  selected?: (element?: SelectedElement) => ReactNode;
};

export function AlphaSelect(props: Props): JSX.Element {
  const {
    onValidate,
    required = false,
    children,
    placeholder,
    selected,
    ...passThrough
  } = props;

  const selectedForSelect: ComponentProps<typeof Select>['selected'] = selected
    ? (element) => selected(element as SelectedElement)
    : undefined;

  type Value = string | number | readonly string[] | undefined;
  const [state, setState] = useState<Value>(
    () => props.value ?? props.defaultValue,
  );
  const [interaction, setInteraction] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const validity = createValidityState({
    valueMissing: Boolean(required && !state),
  });

  const errorMessage = onValidate?.(validity) ?? '';

  useEffect(() => {
    inputRef.current?.setCustomValidity(errorMessage);
  }, [errorMessage]);

  const options = Children.toArray(children);
  if (!required || placeholder)
    options.unshift(
      <Option key="empty-option" value="">
        {placeholder ? (
          <span className="flex items-center gap-2 text-text/disabled alpha-form-input">
            {placeholder}
          </span>
        ) : (
          '\u00A0'
        )}
      </Option>,
    );

  return (
    <div>
      <input
        type="text"
        hidden
        required
        name={props.name}
        value={state ?? ''}
        onInvalid={() => setInteraction(true)}
        // A change event handler is required to suppress a React warning, but not strictly required
        onChange={(event) => setState(event.currentTarget.value)}
      />
      <Select
        {...passThrough}
        error={interaction && !validity.valid}
        selected={selectedForSelect}
        onChange={(value) => {
          if (state === value) return;
          setState(value);
          setInteraction(true);
          props.onChange?.(value);
        }}
        dismiss={Object.assign(
          {
            outsidePress: () => {
              setInteraction(true);
              return true;
            },
          },
          props.dismiss,
        )}
      >
        {options}
      </Select>
      {interaction && errorMessage && (
        <div className="px-padding/x-sm pt-1 text-highlights/red-dark alpha-caption">
          {errorMessage}
        </div>
      )}
    </div>
  );
}
